import { useCallback, useMemo } from 'react';
import { useAppInsight } from '../hooks/useAppInsight';
import { userService } from '../services';

/**
 * Backwards-compatible telemetry hook.
 *
 * Previously this used the Microsoft `@microsoft/applicationinsights-web` SDK
 * directly from the browser (with the instrumentation key exposed in client
 * code). It now delegates to `useAppInsight`, which POSTs events to our own
 * backend (`/api/content-insight/appInsights`).
 *
 * The public API (`trackCustomEvents`, `trackException`) is preserved so all
 * existing call sites keep working without changes.
 */
const useTelemetry = () => {
    const userRole = userService.getUserRole();
    const userName = userService.getUsername();
    const fullName = userService.getUserFullName();

    const { trackEvent, trackException: rawTrackException } = useAppInsight({
        config: { appId: 'coachesdb-web' },
    });

    // Common user info attached to every event/exception
    const user = useMemo(
        () => ({ userName, fullName, role: userRole }),
        [userName, fullName, userRole]
    );

    const trackCustomEvents = useCallback(
        async (event, value, type) => {
            try {
                await trackEvent(event, { ...(value || {}), ...user }, type);
            } catch (err) {
                // Eat exception - telemetry should never break the app
                // eslint-disable-next-line no-console
                console.log('err', err);
            }
        },
        [trackEvent, user]
    );

    const trackException = useCallback(
        async (err, value, type) => {
            try {
                const message =
                    err && err.message ? err.message : String(err || 'Unknown error');
                await rawTrackException(
                    'Exception',
                    { ...(value || {}), message, ...user },
                    type
                );
            } catch {
                // Eat exception
            }
        },
        [rawTrackException, user]
    );

    return { trackException, trackCustomEvents };
};

export default useTelemetry;
